<script setup>
import { onMounted, ref } from 'vue'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import { useAuthStore } from '@/stores/authStore.js'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import { Format } from '@/models/utils/FormatUtils.js'
import ObjectViewer from '@/components/Misc/ObjectViewer.vue'
import ObjectViewerModal from '@/components/Alerts/ObjectViewerModal.vue'
import CopyTextModal from '@/components/Alerts/CopyTextModal.vue'

const authStore = useAuthStore()
const isDeveloper = ref(authStore?.isDeveloper ?? false)
const tenantSettings = ref(null)
const table = ref(null)
const isLoading = ref(false)
const showCopyModal = ref(false)
const dialogTitle = ref('')
const plainDialogMessage = ref('')
const formattedDialogMessage = ref('')
const showObjectModal = ref(false)
const activeData = ref({})

/**
 * Loads roles from the API.
 */
onMounted(() => {
	tenantSettings.value = authStore.getAuth.tenantSettings().scheduled_reports
})

const columns = [
	{ field: "log_type", title: "Log Type", sort: true, search: true, width: '5%' },
	{ field: "name", title: "Name", sort: true, search: true },
	{ field: "report_type", title: "Report Type", sort: true, search: false, width: '5%', minWidth: '180px' },
	{ field: "settings", title: "Settings", sort: false, width: '5%', minWidth: '170px' },
	{ field: "recipients", title: "Recipients", sort: false, search: true, width: '5%', minWidth: '400px'},
	{ field: "created_at", title: "Log Created", sort: true, type: 'date' },
	{ field: "initiator_name", title: "Initiator", sort: true, width: '5%', search: true },
	{ field: "report_changes", title: "Changes", sort: false, search: false, hide: !isDeveloper.value },
	{ field: "frequency", sort: true, search: true, hide: true },
]

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'scheduled-reports/admin/all-logs',
		initialSortColumn: 'created_at',
		initialSortDirection: 'desc',
		searchPlaceholder: 'Search',
		columns: columns,
	})
]

function showTextDialog(title, message) {
	dialogTitle.value = title
	plainDialogMessage.value = message
	formattedDialogMessage.value = message
	showCopyModal.value = true
}

/**
 *
 * @param p_recipients
 */
function showRecipients(p_recipients)
{
	let recipients = '';

	for (let i = 0; i < p_recipients.length; i++)
	{
		if (p_recipients[i].full_name)
		{
			recipients += p_recipients[i].full_name + ' (' +	p_recipients[i].email + ")\r\n";
		}
		else
		{
			recipients += p_recipients[i].email + "\r\n";
		}
	}

	showTextDialog('Recipients', recipients)
}

/**
 * Shows or hides the Object Viewer Modal
 * @param {boolean} p_show True to show modal
 * @param p_data
 */
function toggleObjectViewerModal(p_show, p_data)
{
	if (p_show)
	{
		showObjectModal.value = true
		activeData.value = JSON.parse(p_data)
	}
	else
	{
		showObjectModal.value = false;
	}
}

</script>

<template>
	<LoadingSpinner v-if="isLoading" />
	<div v-if="!isLoading">
		<PortalDataTable
			ref="table"
			:endpoints="endpoints"
		>

			<template #[`name`]="data">
				{{ data.value.scheduled_report.name }}
			</template>

			<template #report_type="data">
					<span class="capitalize">
						{{ data.value.scheduled_report.report_type }}
					</span>
			</template>

			<template #log_type="data">
				<div v-if="data.value.log_type !== 'error'" class="capitalize">
					{{ data.value.log_type }}
				</div>
				<div v-else class="capitalize line-clamp-4 !text-error clickable-text overflow-ellipsis" @click="showTextDialog('Error', data.value.error_message)">
					{{ data.value.log_type }}
				</div>
			</template>

			<template #[`settings`]="data">
				<div v-if="data.value.scheduled_report.settings" class="" ><!-- @click="showTextDialog('Settings', JSON.stringify(data.value.settings, null, 2))" -->
					<ObjectViewer class="max-w-150" :narrow_key="true" :data="data.value.scheduled_report.settings"></ObjectViewer>
				</div>
				<div v-else>
					Default
				</div>

			</template>

			<template #recipients="data">
				<div v-if="data.value.scheduled_report.recipients && data.value.scheduled_report.recipients.length > 0" class="clickable-text overflow-ellipsis whitespace-nowrap" @click="showRecipients(data.value.scheduled_report.recipients)">
					<div v-for="(recipient, index) in data.value.scheduled_report.recipients" :key="index">
						<span :class="[{'line-through': !recipient.is_active}]"><b>{{ recipient.full_name }}</b> ({{ recipient.email }})</span><span v-if="!recipient.is_active"> (Inactive)</span>
					</div>
				</div>
				<div v-else>
					There are no active recipients for this report.
				</div>
			</template>

			<template #initiator_name="data">
				<div class="flex w-full justify-center whitespace-nowrap">
					{{ data.value.user?.full_name ?? 'Portal' }}
				</div>
			</template>

			<template #created_at="data">
				{{ Format.date(data.value.created_at) }}
			</template>

			<template #report_changes="data">
				<div v-if="data.value.report_changes" class="line-clamp-1 clickable-text"  @click="toggleObjectViewerModal(true, data.value.report_changes)">
					Click to view
				</div>
			</template>

		</PortalDataTable>
	</div>

	<ObjectViewerModal
		:data="activeData"
		:show="showObjectModal"
		:title="dialogTitle"
		@close="toggleObjectViewerModal(false)"
	/>

	<CopyTextModal
		:show="showCopyModal"
		:title="dialogTitle"
		:message="plainDialogMessage"
		:formattedMessage="formattedDialogMessage"
		@close="showCopyModal=false"
	/>

</template>

<style scoped>

</style>